import React from 'react';
import classNames from 'classnames';
import { FaCheck } from 'react-icons/fa';
import { HiStar, HiCheckCircle } from 'react-icons/hi';
import { SubscriptionType } from '../../../types/misc';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import Button from '../buttons/Button';
import ModalLayout from '../layouts/ModalLayout';
import { subscriptionFeatures } from '../../../utils/subscriptionUtils';

interface UpgradeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requiredSubscription: SubscriptionType;
  featureName: string;
  onUpgrade?: () => void;
}

export default function UpgradeModal({
  open,
  setOpen,
  requiredSubscription,
  featureName,
  onUpgrade,
}: UpgradeModalProps): JSX.Element {
  const { t } = useBusinessTranslation();

  if (!open) return <></>;

  const subscriptionName = requiredSubscription === SubscriptionType.BASE ? 'Base' : 'Pro';
  const subscriptionColor = requiredSubscription === SubscriptionType.BASE ? 'primary' : 'primary-dark';

  const handleUpgrade = () => {
    onUpgrade?.();
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 w-full max-w-md rounded-[20px] bg-secondary-50 p-6">
      
      <div className="mb-6 text-center">
        <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-${subscriptionColor}-300 bg-opacity-30`}>
          <HiStar className={`h-8 w-8 text-${subscriptionColor}-900`} />
        </div>
        <h3 className="text-xl font-semibold text-primary-900">
          {t('component.modal.upgrade.title')}
        </h3>
        <p className="mt-2 text-sm text-primary-900 text-opacity-80">
          {t('component.modal.upgrade.description', { feature: featureName, plan: subscriptionName })}
        </p>
      </div>

      <div className="mb-6 rounded-lg bg-secondary-200 bg-opacity-50 p-4">
        <div className="flex items-start">
          <HiCheckCircle className="mr-3 mt-0.5 h-5 w-5 text-primary-300" />
          <div>
            <h4 className="font-medium text-primary-900">
              {featureName}
            </h4>
            <p className="text-sm text-primary-900 text-opacity-80">
              {t('component.modal.upgrade.featureDescription', { feature: featureName })}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="mb-3 font-medium text-primary-900">
          {t('component.modal.upgrade.benefits', { plan: subscriptionName })}
        </h4>
        <ul className="space-y-2">
          {subscriptionFeatures
          .filter(feature => {
            if (requiredSubscription === SubscriptionType.FREE) return feature[requiredSubscription];
            if (requiredSubscription === SubscriptionType.BASE) return feature[requiredSubscription] && !feature[SubscriptionType.FREE];
            if (requiredSubscription === SubscriptionType.PRO) return feature[requiredSubscription] && !feature[SubscriptionType.BASE];
            return false;
          })
          .slice(0, requiredSubscription === SubscriptionType.FREE ? 5 : 4)
          .map((feature) => (
            <div key={feature.i18nKey} className="flex items-center gap-2">
              <div className={classNames('flex h-4 w-4 items-center justify-center rounded-full', {
                'bg-primary-300': requiredSubscription !== SubscriptionType.PRO,
                'bg-gray-500': requiredSubscription === SubscriptionType.PRO,
              })}>
                <FaCheck className={classNames('h-2 w-2', {
                  'text-primary-900': requiredSubscription !== SubscriptionType.PRO,
                  'text-gray-900': requiredSubscription === SubscriptionType.PRO,
                })} />
              </div>
              <span className={classNames('text-xs', {
                'text-primary-900': requiredSubscription !== SubscriptionType.PRO,
                'text-gray-900': requiredSubscription === SubscriptionType.PRO,
              })}>
                {t(`page.settings.subscriptions.${feature.i18nKey}`)}
              </span>
            </div>
          ))}
        </ul>
      </div>

      <div className="flex gap-3 justify-between">
        <Button
          variant="secondary"
          onClick={() => setOpen(false)}
          className="text-primary-900 bg-transparent"
        >
          {t('general.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleUpgrade}
        >
          {t('component.modal.upgrade.upgradeButton', { plan: subscriptionName })}
        </Button>
      </div>
    </ModalLayout>
  );
}